import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Vue from 'vue';
import { BG_EXCHANGE_RATE } from '../consts/bg-exchange-rate';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    formatAsPrice,
    getSwiper,
    getSwiperModules,
    normalizeModelName,
} from '../utils';

// Norway tax calculation constants (extracted for clarity)
const NORWAY_TAX_THRESHOLD = 300_000; // Amount after discount above which VAT applies
const NORWAY_VAT_RATE = 0.25; // 25% VAT

const ConfiguratorSteps = {
    MODEL: 'model',
    EDITION: 'edition',
    EXTERIOR: 'exterior',
    INTERIOR: 'interior',
    INTERIOR_MATERIAL: 'interiorMaterial',
    FINANCE: 'finance',
    SUMMARY: 'summary',
    OFFER: 'offer',
};

const totalPrice = {
    cash: 0,
    leasing: 0,
    additionalMethod1: 0,
    additionalMethod2: 0,
    additionalMethod3: 0,
};

gsap.registerPlugin(ScrollTrigger);

Vue.component('configurator-main-component', {
    props: {
        siteId: {
            type: Number,
        },
        darkMode: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
        },
        modelSpecs: {
            type: String,
        },
        isModelMicrositeSelectorVisible: {
            type: Boolean,
            default: false,
        },
        siteHandle: {
            type: String,
        },
        locale: {
            type: String,
        },
    },

    data() {
        return {
            swiper: null,
            exteriors: null,
            interiors: null,
            interiorMaterials: null,
            hoods: null,
            assetsToShow: null,
            subCollection: 'hood',

            selectedEdition: null,
            selectedExterior: null,
            selectedHood: null,
            selectedInterior: null,
            selectedInteriorMaterial: null,
            hoodIndex: 0,
            selectedFinance: 'cash',

            totalPrice: totalPrice,
            tempPrices: null,
            finalPrice: 0,
            finalPriceNumeric: 0,
            configuratorStep: this.isModelMicrositeSelectorVisible
                ? ConfiguratorSteps.MODEL
                : ConfiguratorSteps.EDITION,
            isMergedInteriorSteps: false,

            finalEditionPrice: 0,
            finalExteriorPrice: 0,
            finalHoodPrice: 0,
            finalInteriorPrice: 0,
            finalInteriorMaterialPrice: 0,

            currentSlide: 0,
            activeAccordion: this.isModelMicrositeSelectorVisible
                ? 'model'
                : 'edition',

            // Specs visibility
            specsVisible: false,

            // Chapter functionality
            chapterOpen: false,
            scrollToElement: null,

            isPdfDownloading: false,

            selectedPromotionKeys: [],
            countyPriceMapping: {
                'en-IE': {
                    localization: 'en-IE',
                    showZeros: false,
                },
            },
        };
    },

    computed: {
        toggleChapterClass() {
            return this.chapterOpen ? 'section-chapter--open' : '';
        },

        toggleModelSpecsClass() {
            return this.chapterOpen
                ? 'section-chapter--open model-new-section-chapter--custom-spacing'
                : '';
        },
    },

    async mounted() {
        this.initSelectedItems();
        this.configuratorStep = this.isModelMicrositeSelectorVisible
            ? ConfiguratorSteps.MODEL
            : ConfiguratorSteps.EDITION;

        await this.initSwiper();

        setTimeout(() => {
            this.handleSetStartConfigurationDataLayer();
        }, [1000]);

        EventBus.$on('close-summary-screen', async () => {
            this.configuratorStep = ConfiguratorSteps.SUMMARY;
            await this.initSwiper();
        });

        if (!JSON.parse(this.modelSpecs)) return;
        this.bindSpecsDelegation();
    },

    updated() {
        this.bindSpecsDelegation();
    },

    created() {
        EventBus.$on('complete-santander-configuration', (data) => {
            this.completeConfigurator();
            this.completeSantanderCalculation(data);
        });

        EventBus.$on('openSpecs', () => {
            this.specsSelectedModelName = this.modelName;
            this.selectedSpecsModelType = '';
            this.selectedSpecsModelSubline = '';
            this.selectedSpecsModelSublineAsset = null;
            document.body.style.overflow = 'hidden';
        });

        EventBus.$on('closeSpecs', () => {
            this.chapterOpen = false;
        });
    },

    destroyed() {},

    watch: {
        selectedEdition: function (value) {
            this.updateDataToShow('edition', value);
        },
        selectedExterior: function (value) {
            this.updateDataToShow('exterior', value);
        },
        selectedHood: function (value) {
            this.updateDataToShow('hood', value);
        },
        selectedInterior: function (value) {
            this.updateDataToShow('interior', value);
            if (this.isMergedInteriorSteps) this.updateTempPrice();
        },
        selectedInteriorMaterial: function () {
            this.updateTempPrice();
        },
        configuratorStep: function (value) {
            this.updateTempPrice();
            switch (value) {
                case ConfiguratorSteps.MODEL:
                case ConfiguratorSteps.EDITION:
                case ConfiguratorSteps.EXTERIOR:
                    this.assetsToShow = this.selectedHood
                        ? this.selectedHood.hoodAssets
                        : this.selectedExterior.exteriorAssets;
                    break;
                case ConfiguratorSteps.INTERIOR:
                case ConfiguratorSteps.INTERIOR_MATERIAL:
                case ConfiguratorSteps.FINANCE:
                    this.assetsToShow = this.selectedInterior.interiorAssets;
                    break;
                default:
                    this.assetsToShow = this.selectedHood
                        ? this.selectedHood.hoodAssets
                        : this.selectedExterior.exteriorAssets;
                    break;
            }
        },

        assetsToShow: function () {
            setTimeout(() => {
                if (this.swiper) this.swiper.update();
            }, 0.5);
        },
    },

    methods: {
        parseTransformedImage(image) {
            return JSON.parse(image);
        },

        formatPriceInBg(price) {
            const priceInNumbers = parseFloat(
                String(price).replace(' ', '').replace('.', ''),
            );
            const priceInBg = priceInNumbers * BG_EXCHANGE_RATE;
            return formatAsPrice(priceInBg);
        },

        initSelectedItems() {
            const { editions, financingSettings } = this.data;
            const [firstEdition] = editions;
            const [firstExterior] = firstEdition.exteriors;
            const [firstHood] = firstExterior.hoods;
            const [firstInterior] = firstHood
                ? firstHood.interiors
                : firstExterior.interiors;
            const [firstInteriorMaterial] = firstInterior.interiorMaterials;
            const [firstFinanceOption] = financingSettings.financingOptions;

            this.selectedEdition = firstEdition;
            this.selectedExterior = firstExterior;
            this.selectedHood = firstHood;
            this.selectedInterior = firstInterior;
            this.selectedInteriorMaterial = firstInteriorMaterial;
            this.selectedFinance = firstFinanceOption;
            this.assetsToShow = firstHood
                ? firstHood.hoodAssets
                : firstExterior.exteriorAssets;

            this.isMergedInteriorSteps =
                this.data.generalTranslations.mergeInteriorAndInteriorMaterial;

            this.selectedPromotionKeys =
                this.buildDefaultPromotionKeys(firstEdition);
        },

        normalizePrice(price) {
            if (!price) return 0;
            return Number(price.toString().replace(/[.,]/g, ''));
        },

        formatPrice(price) {
            if (this.locale === 'en-IE') {
                const floatPrice =
                    typeof price === 'string' ? parseFloat(price) : price;
                return floatPrice.toLocaleString('en-IE', {
                    maximumFractionDigits: 0,
                });
            }
            return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        },

        ensureFormattedPrice(raw) {
            if (!raw) return '0';
            const numeric = raw.toString().replace(/\D/g, '');
            if (!numeric) return '0';
            return this.formatPrice(numeric);
        },

        /**
         * Handle push tu data layer for GTM when configuration is started.
         *
         * @returns {Void}
         */
        handleSetStartConfigurationDataLayer() {
            if (!checkCCM19Consent() && !checkOneTrustConsent()) return;

            if (!window.dataLayer) {
                window.dataLayer = window.dataLayer || [];
            }

            window.dataLayer.push({
                event: 'start_configuration',
                car: {
                    name: normalizeModelName(
                        this.data.generalTranslations.modelName,
                        this.$root.handle,
                    ),
                    ids: [this.carEditionId],
                },
            });
        },

        sumPricesObject(firstPricesObject, secondPricesObject) {
            const total = {};

            for (const key in firstPricesObject) {
                if (
                    secondPricesObject.hasOwnProperty(key) &&
                    firstPricesObject.hasOwnProperty(key)
                ) {
                    const normalizedFirstPrice = this.normalizePrice(
                        firstPricesObject[key],
                    );
                    const normalizedSecondPrice = this.normalizePrice(
                        secondPricesObject[key],
                    );

                    total[key] = this.formatPrice(
                        normalizedFirstPrice + normalizedSecondPrice,
                    );
                }
            }

            return total;
        },

        updateTempPrice() {
            const editionPrice = this.sumPricesObject(
                this.selectedEdition.prices,
                this.totalPrice,
            );
            const exteriorPrice = this.sumPricesObject(
                this.selectedExterior.prices,
                editionPrice,
            );
            const hoodPrice = this.sumPricesObject(
                this.selectedHood?.prices ?? {},
                exteriorPrice,
            );
            const interiorPrice = this.sumPricesObject(
                this.selectedInterior.prices,
                Object.keys(hoodPrice).length > 0 ? hoodPrice : exteriorPrice,
            );
            const interiorMaterialPrice = this.sumPricesObject(
                this.selectedInteriorMaterial?.prices ?? {},
                interiorPrice,
            );

            switch (this.configuratorStep) {
                case ConfiguratorSteps.MODEL:
                case ConfiguratorSteps.EDITION:
                    this.tempPrices = editionPrice;
                    break;
                case ConfiguratorSteps.EXTERIOR:
                    this.tempPrices =
                        Object.keys(hoodPrice).length > 0
                            ? hoodPrice
                            : exteriorPrice;
                    break;
                case ConfiguratorSteps.INTERIOR:
                    this.tempPrices = interiorPrice;
                    if (this.isMergedInteriorSteps)
                        this.tempPrices = interiorMaterialPrice;
                    break;
                case ConfiguratorSteps.INTERIOR_MATERIAL:
                    this.tempPrices =
                        Object.keys(interiorMaterialPrice).length > 0
                            ? interiorMaterialPrice
                            : interiorPrice;
                    break;
                default:
                    this.tempPrices =
                        Object.keys(interiorMaterialPrice).length > 0
                            ? interiorMaterialPrice
                            : interiorPrice;
                    break;
            }

            this.applyPromotionDiscounts();
        },

        buildDefaultPromotionKeys(edition) {
            if (!edition || !edition.promotions) return [];
            return edition.promotions.map(
                (p) => p.financingHandle + '::' + p.promotionTitle,
            );
        },

        applyPromotionDiscounts() {
            if (!this.selectedEdition || !this.selectedEdition.promotions)
                return;
            for (const promotion of this.selectedEdition.promotions) {
                const key =
                    promotion.financingHandle + '::' + promotion.promotionTitle;
                if (
                    this.selectedPromotionKeys.includes(key) &&
                    this.tempPrices[promotion.financingHandle] !== undefined
                ) {
                    const current = this.normalizePrice(
                        this.tempPrices[promotion.financingHandle],
                    );
                    this.tempPrices[promotion.financingHandle] =
                        this.formatPrice(current + promotion.discount);
                }
            }
        },

        togglePromotion(promotion) {
            const key =
                promotion.financingHandle + '::' + promotion.promotionTitle;
            const idx = this.selectedPromotionKeys.indexOf(key);
            if (idx > -1) {
                this.selectedPromotionKeys.splice(idx, 1);
            } else {
                this.selectedPromotionKeys.push(key);
            }
            this.updateTempPrice();
        },

        isPromotionSelected(promotion) {
            return this.selectedPromotionKeys.includes(
                promotion.financingHandle + '::' + promotion.promotionTitle,
            );
        },

        getPromotionsForFinancingOption(option) {
            if (!this.selectedEdition || !this.selectedEdition.promotions)
                return [];
            return this.selectedEdition.promotions.filter(
                (p) => p.financingHandle === option.financingHandle,
            );
        },

        getActivePromotionsForFinanceOption(option) {
            return this.getPromotionsForFinancingOption(option).filter((p) =>
                this.isPromotionSelected(p),
            );
        },

        formatPromotionDiscount(discount, currency) {
            if (!discount) return '';
            let abs;
            if (this.locale === 'en-IE') {
                abs = Math.abs(discount).toLocaleString('en-IE', {
                    maximumFractionDigits: 0,
                });
            } else {
                abs = Math.abs(discount)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            }
            const sign = discount < 0 ? '-' : '+';
            if (currency.includes('{price}')) {
                return currency.replace('{price}', sign + abs);
            }
            return `${currency} ${sign}${abs}`;
        },

        updateDataToShow(type, value) {
            switch (type) {
                case 'edition':
                    this.exteriors = value.exteriors;
                    this.updateDataToShow('exterior', value.exteriors[0]);
                    EventBus.$emit(
                        'cyberster-configurator-edition-change',
                        value.uniqueKey,
                    );
                    break;
                case 'exterior':
                    if (value.hoods.length === 0) {
                        this.interiors = value.interiors;
                        this.hoods = [];
                        this.updateDataToShow('interior', value.interiors[0]);
                    } else {
                        this.subCollection = value.subCollection;
                        this.hoods = value.hoods;
                        this.updateDataToShow('hood', value.hoods[0]);
                    }
                    break;
                case 'hood':
                    if (!value) break;
                    this.interiors = value.interiors;
                    this.updateDataToShow('interior', value.interiors[0]);
                    break;
                case 'interior':
                    this.interiorMaterials = value
                        ? value.interiorMaterials
                        : [];
                    break;
                default:
                    console.error('Invalid data type');
                    break;
            }
        },

        checkIfDisabled(item) {
            if (!item.isDisabled) return '';
            return 'configurator-select-type__wrapper-disabled';
        },

        updateSelections(type, value, index) {
            switch (type) {
                case 'edition':
                    this.selectedEdition = value;
                    this.selectedPromotionKeys =
                        this.buildDefaultPromotionKeys(value);
                    this.hoodIndex = 0;
                    return this.updateSelections(
                        'exterior',
                        value.exteriors[0],
                    );
                case 'exterior':
                    this.selectedExterior = value;
                    if (value.hoods.length === 0) {
                        this.interiors = value.interiors;
                        this.selectedHood = null;
                        this.assetsToShow = value.exteriorAssets;
                        return this.updateSelections(
                            'interior',
                            value.interiors[0],
                        );
                    }
                    return this.updateSelections(
                        'hood',
                        value.hoods[this.hoodIndex],
                    );
                case 'hood':
                    if (index !== undefined) this.hoodIndex = index;
                    this.selectedHood = value;
                    if (
                        this.configuratorStep === ConfiguratorSteps.EXTERIOR ||
                        this.configuratorStep === ConfiguratorSteps.EDITION
                    ) {
                        this.assetsToShow = value ? value.hoodAssets : [];
                    }
                    if (!value) break;
                    return this.updateSelections(
                        'interior',
                        value.interiors[0],
                    );
                case 'interior':
                    this.selectedInterior = value;
                    if (this.configuratorStep === ConfiguratorSteps.INTERIOR) {
                        this.assetsToShow = value.interiorAssets;
                        if (this.isMergedInteriorSteps) {
                            return this.updateSelections(
                                'interiorMaterial',
                                value.interiorMaterials[0],
                            );
                        }
                    } else {
                        return this.updateSelections(
                            'interiorMaterial',
                            value.interiorMaterials[0],
                        );
                    }
                    break;
                case 'interiorMaterial':
                    this.selectedInteriorMaterial = value;
                    break;
                case 'finance':
                    this.selectedFinance = value;
                    break;
                default:
                    console.error('Invalid selection type');
                    return null;
            }
            // Update temp prices and compute resulting formatted price string.
            this.updateTempPrice();
            const totalPriceWithCopy = this.getTotalPriceWithCopy();
            const finalPriceString =
                totalPriceWithCopy ||
                this.getCalculatedPriceWithCopy(
                    this.data.generalTranslations.currency,
                );
            return finalPriceString;
        },

        checkSelected(type, item) {
            switch (type) {
                case 'edition':
                    return this.selectedEdition.uniqueKey === item.uniqueKey;
                case 'exterior':
                    return this.selectedExterior.uniqueKey === item.uniqueKey;
                case 'interior':
                    return this.selectedInterior.uniqueKey === item.uniqueKey;
                case 'hood':
                    return this.selectedHood.uniqueKey === item.uniqueKey;
                case 'interiorMaterial':
                    return (
                        this.selectedInteriorMaterial.uniqueKey ===
                        item.uniqueKey
                    );
                case 'finance':
                    if (
                        this.selectedFinance.financingTitle ==
                        item.financingTitle
                    )
                        return `configurator-select-finance${
                            this.darkMode ? '--dark' : '--light'
                        }--active`;
                default:
                    return false;
            }
        },

        changeConfiguratorStep() {
            switch (this.configuratorStep) {
                case ConfiguratorSteps.MODEL:
                    this.configuratorStep = ConfiguratorSteps.EDITION;
                    this.activeAccordion = ConfiguratorSteps.EDITION;
                    break;
                case ConfiguratorSteps.EDITION:
                    this.configuratorStep = ConfiguratorSteps.EXTERIOR;
                    this.activeAccordion = ConfiguratorSteps.EXTERIOR;
                    break;
                case ConfiguratorSteps.EXTERIOR:
                    this.configuratorStep = ConfiguratorSteps.INTERIOR;
                    this.activeAccordion = ConfiguratorSteps.INTERIOR;
                    break;
                case ConfiguratorSteps.INTERIOR:
                    if (this.isMergedInteriorSteps) {
                        this.configuratorStep = ConfiguratorSteps.FINANCE;
                        this.activeAccordion = ConfiguratorSteps.FINANCE;
                    } else if (
                        !this.isMergedInteriorSteps &&
                        !this.interiorMaterials.length
                    ) {
                        this.configuratorStep = ConfiguratorSteps.FINANCE;
                        this.activeAccordion = ConfiguratorSteps.FINANCE;
                    } else {
                        this.configuratorStep =
                            ConfiguratorSteps.INTERIOR_MATERIAL;
                        this.activeAccordion =
                            ConfiguratorSteps.INTERIOR_MATERIAL;
                    }
                    break;
                case ConfiguratorSteps.INTERIOR_MATERIAL:
                    this.configuratorStep = ConfiguratorSteps.FINANCE;
                    this.activeAccordion = ConfiguratorSteps.FINANCE;
                    break;
                case ConfiguratorSteps.FINANCE:
                    this.configuratorStep = ConfiguratorSteps.SUMMARY;
                    this.activeAccordion = ConfiguratorSteps.SUMMARY;
                    this.scrollToTopOfSummary();
                    this.calculateTotalPrice();
                    break;
                case ConfiguratorSteps.SUMMARY:
                    const dataToEmit = this.getDataToEmit();
                    EventBus.$emit('configurator-offer-step', dataToEmit);
                    this.configuratorStep = ConfiguratorSteps.OFFER;
            }

            // Setup dropdown accessibility only on offer step
            this.$nextTick(() => {
                if (this.configuratorStep === ConfiguratorSteps.OFFER) {
                    if (typeof this.setupDropdownsForNewStep === 'function') {
                        this.setupDropdownsForNewStep();
                    } else if (
                        typeof this.refreshDropdownAccessibility === 'function'
                    ) {
                        this.refreshDropdownAccessibility();
                    }
                }
            });

            if (!this.activeAccordion) {
                this.activeAccordion = ConfiguratorSteps.EDITION;
                this.configuratorStep = ConfiguratorSteps.EDITION;
            }
        },

        getDataToEmit() {
            return {
                edition: this.selectedEdition,
                exterior: this.selectedExterior,
                hood: this.selectedHood,
                interior: this.selectedInterior,
                interiorMaterial: this.selectedInteriorMaterial,
                finance: this.selectedFinance,
                totalPrice: this.finalPrice,
                totalPriceNumeric: this.finalPriceNumeric,
                generalTranslations: this.data.generalTranslations,
                offerTranslations: this.data.offerTranslations,
                summaryTranslations: this.data.summaryTranslations,
                editionSettings: this.data.editionSettings,
                exteriorSettings: this.data.exteriorSettings,
                interiorSettings: this.data.interiorSettings,
                interiorMaterialSettings: this.data.interiorMaterialSettings,
                entryId: this.data.entryId,
                subCollection: this.subCollection,
                selectedPromotionKeys: this.selectedPromotionKeys,
            };
        },

        goBack() {
            this.configuratorStep = ConfiguratorSteps.FINANCE;
            this.activeAccordion = ConfiguratorSteps.FINANCE;
        },

        changeConfiguratorStepMenu(step) {
            this.configuratorStep = step;

            // Setup dropdown accessibility only on offer step
            this.$nextTick(() => {
                if (step === ConfiguratorSteps.OFFER) {
                    if (typeof this.setupDropdownsForNewStep === 'function') {
                        this.setupDropdownsForNewStep();
                    } else if (
                        typeof this.refreshDropdownAccessibility === 'function'
                    ) {
                        this.refreshDropdownAccessibility();
                    }
                }
            });
        },

        scrollToTopOfSummary() {
            setTimeout(() => {
                const summary = this.$refs['summaryWrapper'];
                summary.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        },

        calculateTotalPrice() {
            const financeHandle = this.selectedFinance.financingHandle;

            this.finalEditionPrice = this.normalizePrice(
                this.selectedEdition?.prices[financeHandle],
            );
            this.finalExteriorPrice = this.normalizePrice(
                this.selectedExterior?.prices[financeHandle],
            );
            this.finalHoodPrice = this.normalizePrice(
                this.selectedHood?.prices[financeHandle],
            );
            this.finalInteriorPrice = this.normalizePrice(
                this.selectedInterior?.prices[financeHandle],
            );
            this.finalInteriorMaterialPrice = this.normalizePrice(
                this.selectedInteriorMaterial?.prices[financeHandle],
            );

            let rawTotal =
                this.finalEditionPrice +
                this.finalExteriorPrice +
                this.finalHoodPrice +
                this.finalInteriorPrice +
                this.finalInteriorMaterialPrice;

            rawTotal = this.computeNorwayTaxAdjustedPrice(rawTotal);

            this.finalPrice = this.formatPrice(rawTotal);
            this.finalPriceNumeric = rawTotal;
        },

        setActiveAccordion(element) {
            const activeAccordionElement =
                this.activeAccordion === element ? null : element;
            this.activeAccordion = activeAccordionElement;
            this.configuratorStep = activeAccordionElement;

            const accordion = this.$refs['configurator-accordion-' + element];
            setTimeout(() => {
                accordion.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        },

        isActiveAccordion(element) {
            return this.activeAccordion === element
                ? 'configurator__line-vertical--active'
                : '';
        },

        async initSwiper() {
            const { Swiper } = await getSwiper();
            const { Pagination } = await getSwiperModules();

            this.swiper = new Swiper('.configurator-main__slider-content', {
                initialSlide: 0,
                slidesPerView: 1,
                modules: [Pagination],
                pagination: {
                    el: '.swiper-pagination',
                    type: 'progressbar',
                    renderProgressbar: function () {
                        return `<span id="progressBar" class="configurator-main__pagination--active"></span>`;
                    },
                },
                slidesPerGroup: 1,
                centeredSlides: true,
                noSwiping: false,
            });

            this.updateProgressBar(0);

            this.swiper.on('slideChange', (swiper) => {
                this.updateProgressBar(swiper.activeIndex);
            });

            this.swiper.on('update', (swiper) => {
                this.swiper.slideTo(0);
                this.updateProgressBar(swiper.activeIndex);
            });
        },

        updateProgressBar(activeIndex) {
            this.currentSlide = activeIndex;
            const progressBar = document.getElementById('progressBar');
            const totalSlides = this.assetsToShow.length;
            const widthPercent = 100 / totalSlides;

            if (!progressBar) return;

            progressBar.style.transform = `translateX(${
                activeIndex * widthPercent
            }%) scaleX(${1 / totalSlides})`;
        },

        getSelectionStepClass(step) {
            if (step == this.configuratorStep) {
                return this.darkMode
                    ? 'configurator-main__selection-step--active-dark'
                    : 'configurator-main__selection-step--active-light';
            }
        },

        getIsModelActive(modelSlug) {
            if (window.location.pathname.includes(modelSlug)) {
                return 'configurator-model-selector__item--active';
            }
            return '';
        },

        getActiveAccordionClass(step) {
            if (step != this.configuratorStep) return;
            return this.darkMode
                ? 'configurator-accordion__icon-text--active--dark'
                : 'configurator-accordion__icon-text--active--light';
        },

        getTotalPrice(option, currency) {
            const price = this.tempPrices[option.financingHandle];
            if (currency.includes('{price}')) {
                return this.siteHandle === 'mgMotorsBulgaria'
                    ? `${currency.replace(
                          '{price}',
                          price,
                      )} / ${this.formatPriceInBg(price)} лв`
                    : currency.replace('{price}', price);
            }
            return this.siteHandle === 'mgMotorsBulgaria'
                ? `${currency} ${price} / ${this.formatPriceInBg(price)} лв`
                : `${currency} ${price}`;
        },

        getCalculatedPriceWithCopy(currency, data, shouldApplyTaxes = true) {
            let priceRaw =
                data == null
                    ? this.tempPrices[this.selectedFinance.financingHandle]
                    : data;
            const price = this.ensureFormattedPrice(priceRaw);
            const priceTemplate = currency.includes('{price}')
                ? currency
                : `${currency} {price}`;

            if (shouldApplyTaxes === 'false') {
                if (currency.includes('{price}')) {
                    return this.siteHandle === 'mgMotorsBulgaria'
                        ? `${currency.replace(
                              '{price}',
                              price,
                          )} / ${this.formatPriceInBg(price)} лв`
                        : currency.replace('{price}', price);
                }

                return this.siteHandle === 'mgMotorsBulgaria'
                    ? `${currency} ${price} / ${this.formatPriceInBg(price)} лв`
                    : `${currency} ${price}`;
            }
            return this.applyNorwayTaxesIfNeeded(priceTemplate, price);
        },

        getPriceToRender() {
            return this.tempPrices[this.selectedFinance.financingHandle];
        },

        getTotalPriceWithCopy() {
            const priceCopy = this.selectedFinance.priceText;
            const price = this.tempPrices[this.selectedFinance.financingHandle];
            if (!priceCopy) return null;
            return this.applyNorwayTaxesIfNeeded(priceCopy, price);
        },

        calculateNorwayTaxes(price, priceCopy) {
            const discountPrice = this.getNorwayDiscount();
            if (!discountPrice) return priceCopy.replace('{price}', price);

            const normalizedPrice = this.normalizePrice(price);

            if (normalizedPrice - discountPrice > NORWAY_TAX_THRESHOLD) {
                const vatAmountRaw =
                    (normalizedPrice - discountPrice - NORWAY_TAX_THRESHOLD) *
                    NORWAY_VAT_RATE;
                const vatAmount = Math.round(vatAmountRaw);

                const priceToRender = normalizedPrice + vatAmount;
                const formattedPrice = this.formatPrice(priceToRender);
                return priceCopy.replace('{price}', formattedPrice);
            }
            return priceCopy.replace('{price}', price);
        },

        computeNorwayTaxAdjustedPrice(basePrice) {
            if (this.$root.handle !== 'mgMotorsNorway') return basePrice;
            const discountPrice = this.getNorwayDiscount();
            if (!discountPrice) return basePrice;

            const amountAfterDiscount = basePrice - discountPrice;
            if (amountAfterDiscount > NORWAY_TAX_THRESHOLD) {
                const vatAmountRaw =
                    (amountAfterDiscount - NORWAY_TAX_THRESHOLD) *
                    NORWAY_VAT_RATE;
                const vatAmount = Math.round(vatAmountRaw);
                return basePrice + vatAmount;
            }
            return basePrice;
        },

        /**
         * Apply Norway tax logic if conditions met, otherwise simple replacement.
         * @param {String} priceCopy - string containing {price}
         * @param {String|Number} rawPrice - formatted or raw price
         * @returns {String}
         */
        applyNorwayTaxesIfNeeded(priceCopy, rawPrice) {
            if (!priceCopy.includes('{price}')) return priceCopy;
            // Only apply if Norway site & discount logic exists
            if (
                this.$root.handle === 'mgMotorsNorway' &&
                this.getNorwayDiscount() > 0
            ) {
                return this.calculateNorwayTaxes(rawPrice, priceCopy);
            }
            return this.siteHandle === 'mgMotorsBulgaria'
                ? `${priceCopy.replace(
                      '{price}',
                      rawPrice,
                  )} / ${this.formatPriceInBg(rawPrice)} лв`
                : priceCopy.replace('{price}', rawPrice);
        },

        /**
         * Returns normalized Norway discount: hood discount preferred, else edition discount, else 0.
         * @returns {Number}
         */
        getNorwayDiscount() {
            let discount = 0;
            if (this.selectedHood && this.selectedHood.calculationDiscount) {
                discount = this.normalizePrice(
                    this.selectedHood.calculationDiscount,
                );
            } else if (
                this.selectedEdition &&
                this.selectedEdition.calculationDiscount
            ) {
                discount = this.normalizePrice(
                    this.selectedEdition.calculationDiscount,
                );
            }
            return discount;
        },

        getSelectionPrice(type) {
            const financingHandle = this.selectedFinance.financingHandle;
            const basePrice = type.prices[financingHandle];
            if (!type.promotions || !type.promotions.length || !basePrice) {
                return basePrice;
            }
            const isSelectedEdition =
                this.selectedEdition &&
                type.uniqueKey === this.selectedEdition.uniqueKey;
            let numericPrice = this.normalizePrice(basePrice);
            for (const promotion of type.promotions) {
                if (promotion.financingHandle !== financingHandle) continue;
                const key =
                    promotion.financingHandle + '::' + promotion.promotionTitle;
                // For the currently selected edition respect user toggles via
                // selectedPromotionKeys. For other editions apply all promotions
                // (they would all be selected by default upon selection).
                if (
                    isSelectedEdition
                        ? this.selectedPromotionKeys.includes(key)
                        : true
                ) {
                    numericPrice += promotion.discount;
                }
            }
            return this.formatPrice(numericPrice);
        },

        checkIfDisabledExterior(item) {
            const matchHood = item.hoods.find(
                (hood) => hood.uniqueKey === this.selectedHood?.uniqueKey,
            );

            if (!matchHood || !matchHood.isDisabled) return '';
            return 'configurator-select-type__wrapper-disabled';
        },

        checkIfEditionIsSelected(edition) {
            return this.selectedEdition.uniqueKey === edition.uniqueKey
                ? `configurator-select-edition--${
                      this.darkMode ? 'dark' : 'light'
                  }--selected`
                : '';
        },

        /**
         * Returns true only if the edition has at least one highlight row
         * with a non-empty label or description field.
         */
        editionHasHighlights(edition) {
            return edition.editionHighlights?.some(
                (h) => h.label || h.description,
            ) ?? false;
        },

        getPaymentDisclaimer(option) {
            return this.selectedEdition.paymentDisclaimers[
                option.financingHandle
            ];
        },

        bindSpecsDelegation() {
            // The #specification link is CMS-authored content rendered via
            // v-html, so it can't carry a Vue @click binding. Delegate from the
            // component root instead of attaching to the anchor directly: that
            // survives Vue re-renders of the anchor and covers every
            // #specification link (mobile + desktop) without a page-wide
            // listener. Re-run on update so a recreated root gets re-bound.
            const root = this.$el;
            if (!root || root.nodeType !== 1 || root.__specsBound) return;
            root.__specsBound = true;
            root.addEventListener('click', this.handleSpecsAnchorClick);
        },

        handleSpecsAnchorClick(e) {
            if (!e.target.closest('a[href="#specification"]')) return;
            this.toggleSeeFullSpecs(e);
        },

        toggleSeeFullSpecs(e) {
            e.preventDefault();
            const anchor = e.target.closest('a[href="#specification"]');
            this.chapterOpen = !this.chapterOpen;
            EventBus.$emit('openSpecs', anchor);
            // reset scrolltrigger when section opens/closes
            ScrollTrigger.refresh();
            if (this.chapterOpen) {
                gsap.fromTo(
                    this.$el.querySelector('.section-chapter'),
                    { height: 0 },
                    { height: 'auto', duration: 0.6 },
                );
                this.scrollToElement = anchor;
                zenscroll.to(this.$el.querySelector('.section-chapter'));
                // datalayer
                if (this.$root.handle == 'mgMotorsFrance') {
                    dataLayer.push({
                        event: 'contentClic',
                        eventCategory: 'Showroom Content',
                        eventAction: anchor.dataset.section,
                        eventLabel: anchor.dataset.model,
                    });
                }
            } else {
                gsap.fromTo(
                    this.$el.querySelector('.section-chapter'),
                    { height: 'auto' },
                    { height: 0, duration: 0.6, delay: 0.4 },
                );
                zenscroll.to(this.$el, 700);
                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    if (this.$root.handle == 'mgMotorsFrance') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10256326/mg-autos/fr_in002+unique',
                        }); //french
                    }
                    if (this.$root.handle == 'mgMotorsNorway') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10210913/retar0/no_re001+standard',
                        }); //norway
                    }
                }
            }
        },

        getJpegSrcSetFromJsonString(jsonString) {
            const json = JSON.parse(jsonString);
            return json.srcSetJpeg;
        },

        async downloadPdf(event) {
            this.isPdfDownloading = true;
            if (this.selectedEdition === null) {
                alert(
                    'Please complete the configuration before downloading the PDF.',
                );
                return;
            }

            const target = event?.currentTarget;
            const dataset = target?.dataset || {};

            const configurationPayload = {
                siteId: Number(this.siteId),
                model: this.data.generalTranslations.modelName || 'N/A',
                modelName: normalizeModelName(
                    this.data.generalTranslations.modelName,
                ),
                edition: this.selectedEdition?.editionTitle || 'N/A',
                editionHandle:
                    this.selectedEdition?.salesforceInterestEdition || 'N/A',
                engineType: this.selectedEdition?.editionTitle || 'N/A',
                color: this.selectedExterior.selectionTitle || 'N/A',
                interior: this.selectedInterior?.selectionTitle || 'N/A',
                interiorHandle: this.selectedInterior?.uniqueKey,
                interiorImageUrl: this.getJpegSrcSetFromJsonString(
                    this.selectedInterior.interiorAssets[0],
                ),
                colorVATNotice: 'N/A', // TODO: add color VAT notice
                deliveryNotice: 'N/A', // TODO: add delivery notice
                driveType:
                    this.selectedEdition?.salesforceInterestVersion || 'N/A',
                paymentMethod: this.selectedFinance?.financingTitle || 'N/A',
                price: this.selectedFinance.priceText.replace(
                    '{price}',
                    this.finalPrice,
                ),
                energyLabelsImageUrl: 'N/A', // TODO: add energy labels image URL later
                priceDisclaimer: 'N/A', // TODO: add price disclaimer later
                modelImagePath: 'N/A',
                fullModelImageUrl: this.getJpegSrcSetFromJsonString(
                    this.assetsToShow[0],
                ),
                promotions: [null],
                disclaimer: this.selectedEdition.disclaimerText || 'N/A',
                versionDisclaimer: 'N/A',
                paymentDisclaimer:
                    this.selectedFinance.financingDescription || 'N/A',
            };

            const endpoint =
                '/actions/configurator-module/v1/configurator-api/create-configuration-pdf';

            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(configurationPayload),
                });

                if (!response.ok) {
                    throw new Error(
                        `Request failed with status ${response.status}`,
                    );
                }

                const blob = await response.blob();
                const filename = this.buildPdfFilename(configurationPayload);

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('downloadPdf failed', error);
                alert('Unable to generate PDF. Please try again later.');
            } finally {
                this.isPdfDownloading = false;
            }
        },

        buildPdfFilename(configuration) {
            const slugify = (value) =>
                (value || 'configuration')
                    .toString()
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');

            return `${slugify(configuration.model)}-${slugify(
                configuration.edition,
            )}.pdf`;
        },

        pdfDownloadingClass() {
            return this.isPdfDownloading ? 'pdf-button--loading' : '';
        },

        pdfDownloadingTextClass() {
            return this.isPdfDownloading
                ? 'pdf-button__text--hidden'
                : 'pdf-button__text--visible';
        },

        pdfDownloadingSpinnerClass() {
            return this.isPdfDownloading
                ? 'pdf-button__loading-spinner--visible'
                : 'pdf-button__loading-spinner--hidden';
        },
    },
});
